import React, { useState } from "react";
import { format } from "date-fns";
import { pick } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import {
    Table,
    Dropdown,
    Icon,
    Button,
    Confirm,
    Input,
    Modal,
    Message,
    Grid,
    Loader,
} from "semantic-ui-react";
import { makePrivateApiCall } from "utilities/dist/api";
import { toast } from "react-semantic-toasts";

import { parseServerTimestampForClient } from "../../common/helpers";
import { DateFormat } from "utilities/dist/invoice/constants";
import { setDocumentEditorId, updateDocumentName, setInvoiceNotes } from "./actions";

const editorSelector = ({ invoiceDocument }) =>
    pick(invoiceDocument, ["editorDocumentId", "editorIsSaving", "editorError"]);

const DocumentTableRow = ({ doc, isRemovingDoc, isLoading, removeDocumentWhenConfirmed }) => {
    const [confirmingRemoval, setConfirmingRemoval] = useState(0);
    const dispatch = useDispatch();
    const { editorDocumentId, editorIsSaving } = useSelector(editorSelector);
    const [newFileName, setNewFileName] = useState("");
    const [newNote, setNewNote] = useState("");
    const [newFileExtension, setNewFileExtension] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);

    const openPreview = () => {
        setLoadingPreview(true);
        makePrivateApiCall({
            url: `invoice/document/${doc.id}/preview`,
            method: "get",
            responseType: "arraybuffer",
        })
            .then(res => {
                setLoadingPreview(false);
                const file = new Blob([res.data], {
                    type: doc.mimetype,
                });

                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            })
            .catch(() => {
                setLoadingPreview(false);
                toast({
                    description: "Error occurred while generating preview",
                    time: 3000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error",
                });
            });
    };

    return (
        <Table.Row>
            <Table.Cell colSpan="3" collapsing className="clickable" onClick={openPreview}>
                <Icon
                    name={doc.mimetype === "application/pdf" ? "file pdf" : "file image"}
                    className={doc.mimetype === "application/pdf" ? "red" : "green"}
                />{" "}
                <u>{doc.originalname}</u>&nbsp;
                {loadingPreview && <Loader active inline size="mini" />}
            </Table.Cell>
            <Table.Cell>{doc.note}</Table.Cell>
            <Table.Cell>
                {" "}
                {format(parseServerTimestampForClient(doc.updatedAt), DateFormat)}
            </Table.Cell>
            <Table.Cell textAlign="right">
                <Button.Group size="mini">
                    <Button
                        basic
                        color="blue"
                        icon="download"
                        className="no-print"
                        as="a"
                        href={doc.path}
                        target="blank"
                        download
                    />
                    <Button
                        basic
                        color="blue"
                        icon="edit"
                        className="no-print"
                        loading={isRemovingDoc}
                        disabled={isRemovingDoc}
                        onClick={() => {
                            const fragments = doc.originalname.split(".");
                            const extension = fragments.pop();
                            const name = fragments.join(".");
                            setNewFileName(name);
                            setNewNote(doc.note);
                            setNewFileExtension(extension);
                            dispatch(setDocumentEditorId(doc.id));
                        }}
                    />
                    <Button
                        basic
                        color="red"
                        icon="trash"
                        className="no-print"
                        loading={isRemovingDoc}
                        disabled={isRemovingDoc}
                        onClick={() => setConfirmingRemoval(1)}
                    />
                </Button.Group>
                <Confirm
                    header="Change file"
                    cancelButton={{
                        content: "Nevermind",
                        disabled: editorIsSaving,
                        loading: editorIsSaving,
                    }}
                    onConfirm={() => {
                        dispatch(
                            updateDocumentName(
                                editorDocumentId,
                                `${newFileName}.${newFileExtension}`,
                                newNote
                            )
                        ).then(() => {
                            setNewFileName("");
                        });
                    }}
                    open={editorDocumentId === doc.id}
                    content={
                        <Modal.Content>
                            <Grid>
                                <Grid.Column computer={16} mobile={16}>
                                    <label>File name</label>
                                    <Input
                                        fluid
                                        autoFocus
                                        size="large"
                                        value={newFileName}
                                        error={!newFileName}
                                        labelPosition="right"
                                        placeholder="Set a descriptive file name"
                                        label={{ basic: true, content: newFileExtension }}
                                        onChange={e => setNewFileName(e.target.value)}
                                    />
                                    {!newFileName && (
                                        <Message error content="File name can not be empty" />
                                    )}
                                </Grid.Column>
                                <Grid.Column computer={16} mobile={16}>
                                    <label>Note</label>
                                    <Input
                                        fluid
                                        size="large"
                                        value={newNote}
                                        placeholder="Set a note"
                                        onChange={e => setNewNote(e.target.value)}
                                    />
                                </Grid.Column>
                            </Grid>
                        </Modal.Content>
                    }
                    confirmButton={
                        <Button
                            positive
                            loading={editorIsSaving}
                            disabled={editorIsSaving}
                            content="Update"
                        />
                    }
                    onCancel={() => {
                        setNewFileName("");
                        dispatch(setDocumentEditorId(null));
                    }}
                />
                <Confirm
                    header="Remove Document?"
                    cancelButton="Nevermind"
                    onConfirm={() => removeDocumentWhenConfirmed(doc.id)}
                    open={confirmingRemoval > 0}
                    content="Warning! Sure you want to remove this document?"
                    confirmButton={
                        <Button
                            negative
                            loading={isLoading}
                            disabled={isLoading}
                            content="Remove Document"
                        />
                    }
                    onCancel={() => setConfirmingRemoval(0)}
                />
            </Table.Cell>
        </Table.Row>
    );
};

const NoteTableRow = ({ doc, invoiceId }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeletingNote, setIsDeletingNote] = useState(false);

    const dispatch = useDispatch();

    const deleteNote = async () => {
        setIsDeletingNote(true);
        makePrivateApiCall({
            url: `invoice/${invoiceId}/note`,
            method: "DELETE",
            data: doc,
        })
            .then(res => {
                setIsDeletingNote(false);
                dispatch(setInvoiceNotes(res.data.notes));
                setShowDeleteModal(false);
            })
            .catch(() => {
                setIsDeletingNote(false);
                toast({
                    description: "Error deleting a note",
                    time: 3000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error",
                });
            });
    };

    return (
        <Table.Row>
            <Table.Cell colSpan="3" collapsing></Table.Cell>
            <Table.Cell>{doc.note}</Table.Cell>
            <Table.Cell>
                {" "}
                {format(parseServerTimestampForClient(doc.updatedAt), DateFormat)}
            </Table.Cell>
            <Table.Cell textAlign="right">
                <Button.Group size="mini">
                    <Button
                        basic
                        color="red"
                        icon="trash"
                        className="no-print"
                        onClick={() => setShowDeleteModal(true)}
                    />
                </Button.Group>
                <Confirm
                    header="Remove Note?"
                    cancelButton="Nevermind"
                    onConfirm={() => deleteNote()}
                    open={showDeleteModal}
                    content={
                        <div className="tp-10 bp-10 lp-10 rp-10">
                            <p>Warning! Sure you want to remove this note?</p>
                            <Message color="blue">
                                <h4>{doc.note}</h4>
                                <i>
                                    {format(
                                        parseServerTimestampForClient(doc.updatedAt),
                                        DateFormat
                                    )}
                                </i>
                            </Message>
                        </div>
                    }
                    confirmButton={
                        <Button negative loading={isDeletingNote} content="Remove Note" />
                    }
                    onCancel={() => setShowDeleteModal(false)}
                />
            </Table.Cell>
        </Table.Row>
    );
};

export const InvoiceDocumentTable = ({
    isLoading,
    documents,
    notes,
    uploadingDocument,
    removingDocumentsId,
    handleFileUploadClick,
    handleCameraUploadClick,
    removeDocumentWhenConfirmed,
    showNoteModal,
    invoiceId,
}) => {
    let hiddenInput = null;

    const rows = [...(documents ?? []), ...(notes ?? [])]
        .map(item =>
            item.createdAt
                ? {
                      ...item,
                      updatedAt: new Date(item.createdAt).toISOString(),
                  }
                : item
        )
        .sort((a, z) => new Date(z.updatedAt) - new Date(a.updatedAt));

    return (
        <>
            <Table striped className="no-print">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Documents/Notes</Table.HeaderCell>
                        <Table.HeaderCell colSpan="6" textAlign="right">
                            <Dropdown
                                compact={true}
                                text="Upload Doc"
                                icon="upload"
                                floating
                                labeled
                                button
                                className="icon no-print green"
                                basic
                                positive
                            >
                                <Dropdown.Menu direction="left">
                                    <Dropdown.Item
                                        basic
                                        positive
                                        icon="hdd"
                                        className="no-print"
                                        content="Upload From Computer"
                                        loading={!!uploadingDocument}
                                        onClick={() => hiddenInput.click()}
                                    />

                                    <Dropdown.Item
                                        basic
                                        positive
                                        icon="camera"
                                        className="no-print"
                                        content="Upload From Camera"
                                        loading={!!uploadingDocument}
                                        onClick={handleCameraUploadClick}
                                    />
                                </Dropdown.Menu>
                            </Dropdown>
                            <input
                                hidden
                                type="file"
                                ref={el => (hiddenInput = el)}
                                onChange={handleFileUploadClick}
                                onClick={e => (e.target.value = "")}
                            />
                            <Button
                                basic
                                compact={true}
                                color="teal"
                                type="button"
                                onClick={() => showNoteModal()}
                            >
                                <Icon name="sticky note outline" />
                                Add private note
                            </Button>
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                {rows.length > 0 && (
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan="3">Document name</Table.HeaderCell>
                            <Table.HeaderCell>Note</Table.HeaderCell>
                            <Table.HeaderCell>Created on</Table.HeaderCell>

                            <Table.HeaderCell textAlign="right">
                                <span className="no-print">Actions</span>
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                )}

                <Table.Body>
                    {!rows.length ? (
                        <Table.Row>
                            <Table.Cell>No documents/notes on this invoice</Table.Cell>
                        </Table.Row>
                    ) : (
                        rows.map((doc, index) =>
                            doc.filename ? (
                                <DocumentTableRow
                                    doc={doc}
                                    isLoading={isLoading}
                                    key={`invoice_doc_${doc.id}`}
                                    isRemovingDoc={removingDocumentsId === doc.id}
                                    removeDocumentWhenConfirmed={removeDocumentWhenConfirmed}
                                />
                            ) : (
                                <NoteTableRow
                                    doc={doc}
                                    key={`invoice_note_${index}`}
                                    invoiceId={invoiceId}
                                />
                            )
                        )
                    )}
                </Table.Body>
            </Table>
        </>
    );
};
